import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DollarSign, Plus } from "lucide-react";
import { crm } from "./crmApi";
import { Table, Pill, statusTone, Modal, Field, Area, Sel, Loading, Err, fmtDate, fmtMoney } from "./ui";
import { Button } from "@/components/ui/Button";

const PAY_TYPES = ["Deposit", "Progress", "Final", "Other"];
const PAY_METHODS = ["Square", "Cash", "Card", "Zelle", "Cash App", "Other"];

// ---------- PAYMENTS ----------
const EMPTY_PAY = { customerId: "", jobId: "", type: "Deposit", amount: "", method: "Square", date: "" };

export const PaymentsTab = () => {
  const [payments, setPayments] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [error, setError] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(EMPTY_PAY);

  const load = async () => {
    setError(null);
    try {
      const r = await crm("listPayments");
      setPayments(r.payments.reverse());
      crm("listJobs").then(x => setJobs(x.jobs)).catch(() => {});
      crm("listCustomers").then(x => setCustomers(x.customers)).catch(() => {});
    } catch (e) { setError(e); setPayments([]); }
  };
  useEffect(() => { load(); }, []);

  const record = async (e) => {
    e.preventDefault();
    try {
      const r = await crm("recordPayment", form);
      toast.success(`Payment ${r.paymentId} recorded — balance ${fmtMoney(r.remainingBalance)} (${r.paymentStatus})`);
      setShowNew(false); setForm(EMPTY_PAY); load();
    } catch (err) { toast.error(err.message); }
  };

  const collected = (payments || []).reduce((s, p) => s + (parseFloat(p["Total Paid"]) || 0), 0);
  const outstanding = jobs.filter(j => !["Completed", "Cancelled"].includes(j["Job Status"]))
    .reduce((s, j) => s + (parseFloat(j["Remaining Balance"]) || 0), 0);

  if (payments === null) return <Loading />;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-6 text-sm">
          <span className="text-white/60">Collected: <span className="text-weld-glow font-bold">{fmtMoney(collected)}</span></span>
          <span className="text-white/60">Outstanding: <span className="text-amber-400 font-bold">{fmtMoney(outstanding)}</span></span>
        </div>
        <Button data-testid="record-payment-btn" size="sm" onClick={() => setShowNew(true)}><DollarSign size={16} /> Record Payment</Button>
      </div>
      {error ? <Err error={error} /> : (
        <Table
          testId="payments-table"
          cols={[
            { key: "Payment ID", label: "Payment" },
            { key: "Invoice Number", label: "Invoice" },
            { key: "Customer ID", label: "Customer" },
            { key: "Job ID", label: "Job" },
            { key: "Payment Type", label: "Type" },
            { key: "Total Paid", label: "Amount", render: r => fmtMoney(r["Total Paid"]) },
            { key: "Remaining Balance", label: "Balance After", render: r => fmtMoney(r["Remaining Balance"]) },
            { key: "Payment Date", label: "Date", render: r => fmtDate(r["Payment Date"]) },
            { key: "Payment Status", label: "Status", render: r => <Pill tone={statusTone(r["Payment Status"])}>{r["Payment Status"]}</Pill> },
            { key: "Payment Method", label: "Method" },
          ]}
          rows={payments}
        />
      )}

      {showNew && (
        <Modal title="Record Payment" onClose={() => setShowNew(false)} testId="record-payment-modal">
          <form onSubmit={record} className="space-y-4">
            <Sel label="Customer *" required options={[{ value: "", label: "Select customer" }, ...customers.map(c => ({ value: c["Customer ID"], label: `${c["First Name"]} ${c["Last Name"]}`.trim() || c["Customer ID"] }))]}
              value={form.customerId} onChange={e => setForm({ ...form, customerId: e.target.value, jobId: "" })} />
            <Sel label="Job (optional)" options={[{ value: "", label: "None / not tied to a job" }, ...jobs.filter(j => !form.customerId || j["Customer ID"] === form.customerId).map(j => ({ value: j["Job ID"], label: `${j["Job ID"]} — ${j["Job Title"]} (bal ${fmtMoney(j["Remaining Balance"])})` }))]}
              value={form.jobId} onChange={e => setForm({ ...form, jobId: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <Sel label="Type" options={PAY_TYPES} value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} />
              <Field label="Amount *" type="number" min="0" step="0.01" required value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Sel label="Method" options={PAY_METHODS} value={form.method} onChange={e => setForm({ ...form, method: e.target.value })} />
              <Field label="Date" type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
            </div>
            <p className="text-white/40 text-xs">Customer pays via your Square links as usual — record it here to keep balances straight.</p>
            <Button data-testid="record-payment-submit" type="submit" className="w-full">Record Payment</Button>
          </form>
        </Modal>
      )}
    </div>
  );
};

// ---------- FOLLOW-UPS ----------
const FU_STATUSES = ["Pending", "Due", "Completed", "Skipped"];
const EMPTY_FU = { customerId: "", jobId: "", date: "", type: "", notes: "" };

export const FollowUpsTab = () => {
  const [items, setItems] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [error, setError] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(EMPTY_FU);

  const load = async () => {
    setError(null);
    try {
      const r = await crm("listFollowUps");
      setItems(r.followUps.sort((a, b) => String(a["Follow-Up Date"]).localeCompare(String(b["Follow-Up Date"]))));
      crm("listCustomers").then(x => setCustomers(x.customers)).catch(() => {});
    } catch (e) { setError(e); setItems([]); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (followUpId, status) => {
    try { await crm("updateFollowUp", { followUpId, patch: { Status: status } }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(e.message); }
  };

  const create = async (e) => {
    e.preventDefault();
    try { await crm("createFollowUp", form); toast.success("Follow-up created"); setShowNew(false); setForm(EMPTY_FU); load(); }
    catch (err) { toast.error(err.message); }
  };

  if (items === null) return <Loading />;
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button data-testid="new-followup-btn" size="sm" onClick={() => setShowNew(true)}><Plus size={16} /> New Follow-Up</Button>
      </div>
      {error ? <Err error={error} /> : (
        <Table
          testId="followups-table"
          cols={[
            { key: "Follow-Up ID", label: "ID" },
            { key: "Customer ID", label: "Customer" },
            { key: "Job ID", label: "Job" },
            { key: "Follow-Up Date", label: "Date", render: r => fmtDate(r["Follow-Up Date"]) },
            { key: "Follow-Up Type", label: "Type" },
            { key: "Status", label: "Status", render: r => <Pill tone={statusTone(r["Status"])}>{r["Status"]}</Pill> },
            { key: "actions", label: "Mark", render: r => (
              <span onClick={e => e.stopPropagation()} className="flex gap-1.5">
                {FU_STATUSES.filter(s => s !== r["Status"]).map(s => (
                  <button key={s} data-testid={`fu-${s.toLowerCase()}-${r["Follow-Up ID"]}`} onClick={() => setStatus(r["Follow-Up ID"], s)}
                    className="text-xs px-2 py-1 rounded-md border border-white/15 text-white/60 hover:border-weld/50 hover:text-weld-glow transition-colors">{s}</button>
                ))}
              </span>
            )},
          ]}
          rows={items}
        />
      )}

      {showNew && (
        <Modal title="New Follow-Up" onClose={() => setShowNew(false)} testId="new-followup-modal">
          <form onSubmit={create} className="space-y-4">
            <Sel label="Customer *" required options={[{ value: "", label: "Select customer" }, ...customers.map(c => ({ value: c["Customer ID"], label: `${c["First Name"]} ${c["Last Name"]}`.trim() || c["Customer ID"] }))]}
              value={form.customerId} onChange={e => setForm({ ...form, customerId: e.target.value })} />
            <Field label="Date *" type="date" required value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
            <Field label="Type" placeholder="e.g. Quote follow-up call" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} />
            <Area label="Notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            <Button data-testid="new-followup-submit" type="submit" className="w-full">Create Follow-Up</Button>
          </form>
        </Modal>
      )}
    </div>
  );
};

// ---------- WARRANTIES ----------
const WAR_STATUSES = ["Active", "Expiring Soon", "Claimed", "Expired", "Void"];

export const WarrantiesTab = () => {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setError(null);
    try { const r = await crm("listWarranties"); setItems(r.warranties.reverse()); }
    catch (e) { setError(e); setItems([]); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (warrantyId, status) => {
    try { await crm("updateWarranty", { warrantyId, patch: { "Warranty Status": status } }); toast.success(`Warranty ${status}`); load(); }
    catch (e) { toast.error(e.message); }
  };

  if (items === null) return <Loading />;
  return (
    <div className="space-y-4">
      <p className="text-white/40 text-sm">90-day workmanship warranties are created automatically when a job is marked complete. The daily check flags warranties nearing expiration.</p>
      {error ? <Err error={error} /> : (
        <Table
          testId="warranties-table"
          cols={[
            { key: "Warranty ID", label: "Warranty" },
            { key: "Customer ID", label: "Customer" },
            { key: "Job ID", label: "Job" },
            { key: "Job Description", label: "Job", render: r => <span className="line-clamp-2 max-w-[220px] inline-block">{r["Job Description"]}</span> },
            { key: "Warranty Start Date", label: "Start", render: r => fmtDate(r["Warranty Start Date"]) },
            { key: "Warranty Expiration Date", label: "Expires", render: r => fmtDate(r["Warranty Expiration Date"]) },
            { key: "Warranty Status", label: "Status", render: r => (
              <span onClick={e => e.stopPropagation()} className="flex items-center gap-2">
                <Pill tone={statusTone(r["Warranty Status"])}>{r["Warranty Status"]}</Pill>
                <Sel label="" options={WAR_STATUSES} value={r["Warranty Status"]} onChange={e => setStatus(r["Warranty ID"], e.target.value)} />
              </span>
            )},
          ]}
          rows={items}
        />
      )}
    </div>
  );
};
